import { useMutation } from '@apollo/client';
import { MUTATION } from '../gql-operations';
import { invoke } from '@tauri-apps/api/core';
import { uploadMultipartToS3 } from '../utils/s3Upload';

/**
 * Tauri-specific multipart upload hook
 * Uses native Rust backend for uploading large files (>100MB)
 */
function useMultipartUploadTauri() {
    const [initiateMultipartUpload] = useMutation(MUTATION.INITIATE_MULTIPART_UPLOAD);
    const [completeMultipartUpload] = useMutation(MUTATION.COMPLETE_MULTIPART_UPLOAD);
    const [abortMultipartUpload] = useMutation(MUTATION.ABORT_MULTIPART_UPLOAD);

    /**
     * Upload a large file using multipart upload via Tauri
     * @param {File} file - The file to upload
     * @param {string} fileName - Original filename
     * @param {Object} message - Optional message object for progress tracking
     * @param {Function} onProgress - Progress callback (percentage)
     * @returns {Promise<{url: string, key: string}>}
     */
    const uploadMultipartTauri = async (file, fileName, message, onProgress, options = {}) => {
        const { signal } = options;
        console.log('[useMultipartUploadTauri] Starting multipart upload for:', fileName);

        if (signal?.aborted) {
            throw new Error('Upload cancelled');
        }

        // Calculate number of parts (50MB chunks)
        const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB
        const partCount = Math.ceil(file.size / CHUNK_SIZE);

        console.log(`[useMultipartUploadTauri] File size: ${file.size}, Parts: ${partCount}`);

        let uploadIdToAbort = null;
        let keyToAbort = null;

        try {
            // Step 1: Initiate multipart upload (get signed URLs from server)
            const initiateResponse = await initiateMultipartUpload({
                variables: {
                    data: {
                        fileName: fileName,
                        fileType: file.type,
                        fileSize: file.size,
                        partCount: partCount
                    }
                }
            });

            if (!initiateResponse?.data?.initiateMultipartUpload) {
                throw new Error('Failed to initiate multipart upload');
            }

            const { uploadId, key, parts } = initiateResponse.data.initiateMultipartUpload;
            uploadIdToAbort = uploadId;
            keyToAbort = key;

            if (signal?.aborted) {
                throw new Error('Upload cancelled');
            }

            console.log(`[useMultipartUploadTauri] Upload initiated:`, {
                uploadId,
                key,
                partCount: parts.length
            });

            // Step 2: Get file path (must be an absolute native path)
            const isAbsoluteNativePath = path =>
                Boolean(path && typeof path === 'string' && (path.startsWith('/') || /^[a-zA-Z]:[\\/]/.test(path)));

            let filePath = isAbsoluteNativePath(file?.path) ? file.path : null;

            // If no valid native path, convert file content to temp file
            if (!filePath) {
                if (file.size > 50 * 1024 * 1024) {
                    throw new Error('Native multipart upload requires an absolute filesystem path for files > 50MB. Use uploadMultipart instead.');
                }
                console.log('[useMultipartUploadTauri] No file path, creating temp file...');
                const arrayBuffer = await file.arrayBuffer();
                const uint8Array = new Uint8Array(arrayBuffer);

                const fileDropResult = await invoke('handle_file_content', {
                    fileContent: Array.from(uint8Array),
                    fileName: file.name,
                    tempFileName: `temp_${Date.now()}_${file.name}`
                });

                if (!fileDropResult || !fileDropResult.path) {
                    throw new Error('Failed to create temp file');
                }

                filePath = fileDropResult.path;
            }

            console.log(`[useMultipartUploadTauri] File path: ${filePath}`);

            // Step 3: Upload all parts via Rust backend
            const completedParts = await uploadMultipartToS3(
                filePath,
                parts,
                file.type,
                (percentage) => {
                    if (signal?.aborted) return;
                    if (onProgress) onProgress(percentage);
                },
                { signal, uploadId: options.uploadId }
            );

            if (signal?.aborted) {
                throw new Error('Upload cancelled');
            }

            console.log('[useMultipartUploadTauri] All parts uploaded, completing...');

            // Step 4: Complete multipart upload on server
            const completeResponse = await completeMultipartUpload({
                variables: {
                    data: {
                        key: key,
                        uploadId: uploadId,
                        parts: completedParts
                    }
                }
            });

            if (!completeResponse?.data?.completeMultipartUpload) {
                throw new Error('Failed to complete multipart upload');
            }

            const { url } = completeResponse.data.completeMultipartUpload;

            console.log('[useMultipartUploadTauri] Multipart upload completed successfully');

            return {
                url: url,
                key: key
            };

        } catch (error) {
            console.error('[useMultipartUploadTauri] Upload failed:', error);

            if (keyToAbort && uploadIdToAbort) {
                try {
                    await abortMultipartUpload({
                        variables: {
                            data: {
                                key: keyToAbort,
                                uploadId: uploadIdToAbort
                            }
                        }
                    });
                } catch (abortError) {
                    console.error('[useMultipartUploadTauri] Failed to abort upload on server:', abortError);
                }
            }

            throw error;
        }
    };

    // Check if file should use multipart (>100MB)
    const shouldUseMultipart = (file) => {
        const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100MB
        return file.size > MULTIPART_THRESHOLD;
    };

    return {
        uploadMultipartTauri,
        shouldUseMultipart
    };
}

export default useMultipartUploadTauri;
